import React, { useCallback, useState, useMemo } from "react";
import { Box, Typography, Divider, Tooltip, Dialog, IconButton, DialogTitle, DialogContent, Checkbox, Card } from "@mui/material";
import type { ISavedRecipes } from "../types/recipe";
import { Delete, LibraryAdd as LibraryAddIcon, ArrowBack, Edit, Article } from "@mui/icons-material";
import axiosService from "../utils/axios";
import CustomButton from "./default/CustomButton";
import type { IChapter } from "../types/chapter";
import { fetchChapters as fetchChaptersHelper } from "../utils/helpers/chapterHelper";
import EditRecipe from "./EditRecipe";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store";
import { reset as resetEditData } from "../store/slices/editData";
import SavedRecipe from "./SavedRecipe";
import ChapterAddDialog from "./ChapterAddDialog";
import { scrollbarCss } from "../utils/themeScrollbar";

interface ILibraryRecipeProps {
    recipe: ISavedRecipes;
    chapter?: IChapter;
    setCurrentRecipe: (val: null) => void;
    getData: () => void;
}

const LibraryRecipe: React.FC<ILibraryRecipeProps> = ({ recipe, chapter, setCurrentRecipe, getData }) => {
    const dispatch = useDispatch();

    const authState = useSelector((state: RootState) => state.auth);

    const [error, setError] = useState("");

    if (error) console.log(error); //TODO: Handle errors.

    const [recipeData, setRecipeData] = useState<ISavedRecipes>(recipe);
    const [deleteAll, setDeleteAll] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openChapterModal, setOpenChapterModal] = useState(false);
    const [editMode, setEditMode] = useState(false);

    // Pull editData from the Redux store (slice)
    const editData = useSelector((state: RootState) => state.editData);

    const handleBack = useCallback(() => {
        setCurrentRecipe(null);
        getData();
    }, [getData, setCurrentRecipe]);

    const openDeleteConfirmation = useCallback(() => {
        setOpenDeleteModal(true);
    }, []);

    const openChapterUpdate = useCallback(() => {
        fetchChaptersHelper().then((res) => {
            setOpenChapterModal(true);
        });
    }, []);

    const deleteFunctionMap = useMemo(
        () => ({
            deleteRecipeFromAll: () => {
                axiosService.post(`${process.env.REACT_APP_API_URL}/recipes/delete`, { recipeId: recipe["_id"] }).then((res) => {
                    if (res.status !== 200) {
                        setError("There was a problem during deletion of recipe from Library. Error: " + res.data.body);
                        console.log("Delete Error:", res.data.body);
                    }
                    handleBack();
                });
            },
            deleteRecipeFromChapter: () => {
                if (chapter) {
                    axiosService
                        .post(`${process.env.REACT_APP_API_URL}/chapters/deleteRecipe`, {
                            chapterId: chapter["_id"],
                            recipeId: recipe["_id"],
                        })
                        .then((res) => {
                            if (res.status !== 200) {
                                setError("There was a problem during deletion of recipe from chapter. Error: " + res.data.body);
                                console.log("Delete Chapter Error:", res.data.body);
                            }
                            handleBack();
                        });
                }
            },
        }),
        [recipe, chapter, handleBack]
    );

    const handleDelete = useCallback(() => {
        const functionName = deleteAll || !chapter ? "deleteRecipeFromAll" : "deleteRecipeFromChapter";
        deleteFunctionMap[functionName]();
    }, [deleteAll, chapter, deleteFunctionMap]);

    const updateRecipe = useCallback(() => {
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/recipes/update`, {
                name: editData.name,
                description: editData.description,
                ingredients: editData.ingredients,
                procedure: editData.procedure,
                tags: editData.tags,
                recipeId: recipe._id,
            })
            .then((res) => {
                getData();
                // refresh local recipe snapshot
                recipe["name"] = res.data.body.name;
                recipe["description"] = res.data.body.description;
                recipe["ingredients"] = res.data.body.ingredients;
                recipe["procedure"] = res.data.body.procedure;
                recipe["tags"] = res.data.body.tags;
                setEditMode(false);
            });
    }, [editData, recipe, getData]);

    return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
                sx={{
                    overflow: "auto",
                    ...scrollbarCss,
                    height: "calc(100dvh - 89px)",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Box sx={{ maxWidth: 1200, width: "100vw", my: 2 }}>
                    <Card sx={{ display: "flex", flexDirection: "row", p: 0.5 }}>
                        <Box sx={{ display: "flex", height: "32px" }}>
                            <Tooltip title="Back" placement="right">
                                <IconButton onClick={handleBack}>
                                    <ArrowBack />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: "flex", height: "32px", justifyContent: "right", width: "100%", marginBottom: "4px" }}>
                            {editMode ? (
                                <Tooltip title="View" placement="top">
                                    <IconButton
                                        onClick={() => {
                                            // Reset slice state back to the recipe snapshot and exit edit mode
                                            dispatch(
                                                resetEditData({
                                                    tags: { old: recipe.tags ?? [], new: [] },
                                                    name: recipe.name ?? "Unknown",
                                                    description: recipe.description ?? "",
                                                    ingredients: recipe.ingredients ?? [],
                                                    procedure: recipe.procedure ?? [],
                                                })
                                            );
                                            setEditMode(false);
                                        }}
                                    >
                                        <Article />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                authState.account &&
                                recipeData.user?.username === authState.account.username && (
                                    <Tooltip title="Edit" placement="top">
                                        <IconButton
                                            onClick={() => {
                                                dispatch(
                                                    resetEditData({
                                                        tags: { old: recipe.tags ?? [], new: [] },
                                                        name: recipe.name ?? "Unknown",
                                                        description: recipe.description ?? "",
                                                        ingredients: recipe.ingredients ?? [],
                                                        procedure: recipe.procedure ?? [],
                                                    })
                                                );
                                                setEditMode(true);
                                            }}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </Tooltip>
                                )
                            )}
                            <Tooltip title="Save to Chapters" placement="top">
                                <IconButton onClick={openChapterUpdate}>
                                    <LibraryAddIcon />
                                </IconButton>
                            </Tooltip>

                            {authState.account && recipeData.user?.username === authState.account.username && (
                                <Tooltip title="Delete" placement="top">
                                    <IconButton onClick={openDeleteConfirmation}>
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Card>
                    <Divider />

                    {editMode ? (
                        <EditRecipe editData={editData} submit={updateRecipe} />
                    ) : (
                        <SavedRecipe recipeData={recipeData} setRecipeData={setRecipeData} />
                    )}
                </Box>
            </Box>

            <Dialog
                open={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false);
                    getData();
                }}
            >
                <Box>
                    <DialogTitle sx={{ textAlign: "center" }}>Delete Recipe</DialogTitle>
                    <DialogContent dividers>
                        <Typography>
                            Are you sure you want to delete this recipe from {chapter !== undefined ? chapter.name : "All Recipes"}?
                        </Typography>
                        {chapter && (
                            <Typography>
                                <Checkbox checked={deleteAll} onChange={() => setDeleteAll(!deleteAll)} /> Delete all instances of this
                                recipe in Library.
                            </Typography>
                        )}
                        <CustomButton onClick={handleDelete} label="Continue" sx={{ color: "white", width: "100%", marginTop: "25px" }} />
                    </DialogContent>
                </Box>
            </Dialog>

            {openChapterModal && <ChapterAddDialog setOpenModal={setOpenChapterModal} recipe={recipe} saveable={false} />}
        </Box>
    );
};

export default React.memo(LibraryRecipe);
